<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed } from "vue"
import { useI18n } from "vue-i18n"
import { storeToRefs } from "pinia"
import { useSubscriptionFormStore } from "@modules/abonnement/stores/subscription-form.store"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            title: "Récapitulatif",
            subtitle:
                "Vérifiez les informations avant de procéder au paiement.",
            offers_and_options: "Offre et options",
            commitment: "Avec engagement 12 mois",
            no_commitment: "Sans engagement",
            sources_count: "Nombre de sources",
            total_amount: "Montant total",
            month: "mois",
            year: "an",
            back: "Retour",
            cta_payment: "Procéder au paiement sécurisé",
            redirecting: "Redirection en cours...",
            error_redirect: "Erreur lors de la redirection.",
        },
    },
})

// ================================
// COMPOSABLES
// ================================
const subscriptionFormStore = useSubscriptionFormStore()
const {
    getSummary,
    getSourcesPlans,
    offerSourceSelected,
    commitment,
    isFetchingRedirectToPayment,
    hasErrorRedirectToPayment,
    errorRedirectToPayment,
} = storeToRefs(subscriptionFormStore)
const { redirectToPayment } = subscriptionFormStore

// ================================
// PROPS & EMITS
// ================================
const emit = defineEmits<{
    (e: "back"): void
}>()

// ================================
// BUSINESS LOGIC
// ================================
/** Formate un montant en devise EUR (fr-FR). */
function formatPrice(value: number): string {
    return new Intl.NumberFormat("fr-FR", {
        style: "currency",
        currency: "EUR",
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    }).format(value)
}

// ================================
// COMPUTED
// ================================
/** Nombre de sources du plan sélectionné. */
const getSourcesCount = computed(() => {
    const plans = getSourcesPlans.value
    const selectedId = offerSourceSelected.value
    const plan = plans.find((p) => p.id === selectedId) ?? plans[0]
    return plan?.sources ?? 0
})

/** Montant total (mensuel ; x12 pour affichage annuel si engagement). */
const getTotal = computed(() => {
    const details = getSummary.value.details
    const sum = details.reduce((acc, d) => acc + Number(d.price), 0)
    return sum.toFixed(2)
})

/** Unité d'affichage des prix (an si engagement, sinon mois). */
const getPriceUnit = computed(() => (commitment.value ? t("year") : t("month")))

/** Multiplicateur pour affichage (x12 si engagement). */
const getPriceMultiplier = computed(() => (commitment.value ? 12 : 1))

/** Libellé engagement. */
const getCommitmentLabel = computed(() =>
    commitment.value ? t("commitment") : t("no_commitment")
)

// ================================
// INTERACTIONS
// ================================
/** Redirige vers la page de paiement Stripe. */
function onProceedToPayment(): void {
    redirectToPayment()
}
</script>

<template>
    <BaseCard
        class="subscription-form-recap"
        :pt="{
            header: 'flex flex-col gap-4 justify-center items-center p-6',
            content: 'flex flex-col gap-8 px-6 md:px-10 pb-8',
        }"
    >
        <template #header>
            <h1 class="text-xl font-semibold text-primary-900 text-center">
                {{ t("title") }}
            </h1>
            <p>
                {{ t("subtitle") }}
            </p>
        </template>
        <template #content>
            <div class="flex flex-col gap-6 max-w-2xl mx-auto">
                <h2 class="text-xl font-medium text-primary-800">
                    {{ t("offers_and_options") }}
                </h2>

                <div
                    class="flex flex-col gap-3 border border-primary-100 rounded-lg p-4 bg-primary-50/50"
                >
                    <p class="font-medium text-primary-900">
                        {{ getSummary.plan }} • {{ getCommitmentLabel }}
                    </p>
                    <p class="text-primary-700">
                        {{ t("sources_count") }} :
                        <strong>{{ getSourcesCount }}</strong>
                    </p>
                    <div
                        class="flex flex-col gap-2 pt-2 border-t border-primary-200"
                    >
                        <div
                            v-for="(detail, index) in getSummary.details"
                            :key="index"
                            class="flex flex-row justify-between items-center text-sm gap-8"
                        >
                            <span class="text-primary-800 min-w-0">{{
                                detail.name
                            }}</span>
                            <div
                                class="flex flex-col items-end shrink-0 text-right"
                            >
                                <span
                                    class="font-medium text-primary-900 whitespace-nowrap"
                                >
                                    {{ index !== 0 ? "+ " : ""
                                    }}{{
                                        formatPrice(
                                            Number(detail.price) *
                                                getPriceMultiplier
                                        )
                                    }}
                                    / {{ getPriceUnit }}
                                </span>
                                <span
                                    v-if="commitment"
                                    class="text-xs text-primary-600"
                                >
                                    {{ formatPrice(Number(detail.price)) }} /
                                    {{ t("month") }}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                <div
                    class="flex flex-row flex-wrap items-center justify-between gap-2 py-4 border-t border-primary-200"
                >
                    <p class="text-xl font-medium text-primary-800">
                        {{ t("total_amount") }}
                    </p>
                    <div class="flex flex-col items-end text-right">
                        <p class="text-xl font-bold text-primary-900">
                            {{
                                formatPrice(
                                    Number(getTotal) * getPriceMultiplier
                                )
                            }}
                            / {{ getPriceUnit }}
                        </p>
                        <p v-if="commitment" class="text-sm text-primary-600">
                            {{ formatPrice(Number(getTotal)) }} /
                            {{ t("month") }}
                        </p>
                    </div>
                </div>

                <div
                    class="w-full flex flex-col-reverse md:flex-row gap-4 justify-between pt-4"
                >
                    <BaseButton
                        :label="t('back')"
                        severity="secondary"
                        outlined
                        :disabled="isFetchingRedirectToPayment"
                        @click="emit('back')"
                    />
                    <BaseButton
                        :label="t('cta_payment')"
                        :loading="isFetchingRedirectToPayment"
                        :disabled="isFetchingRedirectToPayment"
                        @click="onProceedToPayment"
                    />
                </div>
                <BaseMessage v-if="hasErrorRedirectToPayment" severity="error">
                    {{ errorRedirectToPayment?.message ?? t("error_redirect") }}
                </BaseMessage>
            </div>
        </template>
    </BaseCard>
</template>
